/**
 * The card a second call shows while the person is on a call.
 *
 * Its own floating card rather than a takeover of the call window: the call in
 * front is still the conversation, and the window may be tucked into the corner
 * pill. The card sits on top of both, at the same place every time, and asks one
 * question: hold this call and take the new one, or turn it down.
 *
 * Not answering is a choice too. The offer rings for the desk's ring time and
 * then goes on to the next operator, forward or voicemail, exactly as a missed
 * call does - the card simply goes away with it.
 */
import { createPortal } from "react-dom";
import { ListTree, Phone, PhoneOff } from "lucide-react";
import { Button, Card } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { formatPhone } from "../../../lib/phone";
import { Avatar } from "../../ui";
import { useCallAlert } from "../incoming/useCallAlert";
import type { AnswerBlock } from "./busyMode";
import { useKnockTone } from "./knockTone";
import type { KnockView } from "./useCallWaiting";

/** The sentence under the buttons when Answer is not offered. */
const BLOCK_KEY: Record<Exclude<AnswerBlock, "ok">, string> = {
  not_yet: "calls.waiting.notYet",
  cannot_hold: "calls.waiting.cannotHold",
  one_on_hold: "calls.waiting.oneOnHold",
};

export function KnockCard({
  knock,
  block,
  holdsFront,
  onAnswer,
  onDecline,
}: {
  knock: KnockView;
  block: AnswerBlock;
  /** Answering puts the call in front on hold (it is live), so the button says so. */
  holdsFront: boolean;
  onAnswer: () => void;
  onDecline: () => void;
}) {
  const t = useT();
  // The short tone, not the ring: the person is talking to somebody.
  useKnockTone(!knock.pending);

  const best = knock.matches[0] ?? null;
  const name = best?.name ?? t("calls.incoming.unknownCaller");
  const number = knock.from ? formatPhone(knock.from) : t("calls.incoming.withheld");
  // The tab title and the system notice, as for any ring: the call window may
  // be in another tab's shadow while the person reads something else.
  useCallAlert({ caller: name, number });

  const hint = knock.error
    ? t(`calls.waiting.error.${knock.error}`)
    : block !== "ok"
      ? t(BLOCK_KEY[block])
      : null;

  return createPortal(
    <div
      className="fixed inset-x-4 top-4 z-[96] sm:inset-x-auto sm:right-6 sm:top-6 sm:w-[360px]"
      role="alertdialog"
      aria-live="assertive"
      aria-label={t("calls.waiting.title")}
    >
      <Card className="p-4 shadow-elevated">
        <p className="crm-live-st !mt-0 text-[11.5px]">
          <i />
          {t("calls.waiting.title")}
        </p>
        <div className="mt-3 flex items-center gap-3">
          <Avatar name={name} size={40} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[15px] font-semibold">{name}</p>
            <p className="truncate text-[12.5px] tabular-nums text-muted-foreground">{number}</p>
          </div>
        </div>
        {knock.intentDepartment ? (
          <span className="crm-ctag mt-2.5">
            <ListTree className="size-2.5" />
            {knock.intentDepartment}
          </span>
        ) : null}
        {hint ? <p className="mt-3 text-[12.5px] text-muted-foreground">{hint}</p> : null}
        <div className="mt-3.5 flex gap-2">
          <Button
            type="button"
            variant="destructive"
            size="md"
            className="flex-1"
            onClick={onDecline}
            disabled={knock.pending}
          >
            <PhoneOff className="size-4" />
            {t("calls.waiting.decline")}
          </Button>
          {block === "ok" ? (
            <Button
              type="button"
              variant="success"
              size="md"
              className="flex-1"
              onClick={onAnswer}
              loading={knock.pending}
            >
              <Phone className="size-4" />
              {holdsFront ? t("calls.waiting.holdAndAnswer") : t("calls.waiting.answer")}
            </Button>
          ) : null}
        </div>
      </Card>
    </div>,
    document.body,
  );
}
